import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Container, Row, Col, Placeholder, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faHeartbeat, faVial, faWaveSquare, faXRay } from "@fortawesome/free-solid-svg-icons";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { getDaysBetweenDates, getLocalDateTimeStr } from "../../../../utils/time";
import { ECG12LeadSection } from "../common/ECG12LeadSection";
import PersonalIdentifier from "../../../common/PersonalIdentifier";
import { LabsModal } from "../../Labs";
import { convertScanMultiLabsToLabArray } from "../../../../utils/lab-data-adapter";
import { Lab } from "../../../../amplify/api-gateway/query";
import "./PhysiologyDashboard.scss";

export const PhysiologyDashboardRawData = (props: {
    patientVisit: PatientVisit;
    scanData: any;
}) => {
    const [selectedSection, setSelectedSection] = useState<string | null>(null);
    const [transformedLabs, setTransformedLabs] = useState<Lab[][]>([]);
    const [showLabsModal, setShowLabsModal] = useState(false);

    // Transform labs data when scanData changes
    useEffect(() => {
        if (props.scanData?.labs) {
            setTransformedLabs(convertScanMultiLabsToLabArray(props.scanData.labs));
        }
    }, [props.scanData]);

    if (!props.patientVisit) return <></>;

    // ---------- Utility Functions ----------
    const getItemCount = (title: string) => {
        if (!props.scanData) return 0;

        switch (title) {
            case "Labs":
                return Object.keys(props.scanData.labs || {}).length;
            case "Echo":
                return props.scanData.echo?.length || 0;
            case "ECG":
                return props.scanData.ecg_12L?.length || 0;
            case "Vital Signs":
                return Object.keys(props.scanData.vs_stats_by_csn || {}).length;
            case "Chest Radiology":
            case "Abdomen/Pelvis Radiology":
            case "Head Radiology":
                return (
                    props.scanData.imaging?.filter((study: any) => {
                        if (title === "Chest Radiology") return study.regions.includes("CHEST");
                        if (title === "Abdomen/Pelvis Radiology")
                            return study.regions.includes("ABDOMEN") || study.regions.includes("PELVIS");
                        if (title === "Head Radiology") return study.regions.includes("HEAD");
                        return false;
                    }).length || 0
                );
            default:
                return 0;
        }
    };

    const noDataIndicator = (
        <Container className="text-center text-muted my-4">
            <FontAwesomeIcon icon={faBolt} size="2x" />
            <div>No Data Available</div>
        </Container>
    );

    const getVitalBox = (vs: any, vital: string) => {
        const vitalKey = vital.toLowerCase();
        if (!vs) return;
        return (
            <div className="vs-box mb-3 p-2 border rounded">
                <strong>{vital.toUpperCase()}</strong>
                {vs[vitalKey] ? (
                    <>
                        <div>Mean: {Math.round(vs[vitalKey].mean * 100) / 100}</div>
                        <div>Median: {vs[vitalKey].median}</div>
                        <div>P5-P95: {vs[vitalKey].p5} - {vs[vitalKey].p95}</div>
                        <div>n: {vs[vitalKey].count}</div>
                    </>
                ) : (
                    <div><i>No Data</i></div>
                )}
            </div>
        );
    };

    // ---------- Section Rendering ----------
    const renderSection = (title: string) => {
        const { scanData, patientVisit } = props;
        const count = getItemCount(title);
        if (!scanData) {
            return (
                <Placeholder as="p" animation="glow">
                    <Placeholder xs={12} />
                </Placeholder>
            );
        }

        if (count === 0) return noDataIndicator;

        switch (title) {
            case "Labs":
                // Labs now open in the unified LabsModal
                setShowLabsModal(true);
                setSelectedSection(null);
                return null;

            case "ECG":
                return (
                    <div className="section-list">
                        {scanData.ecg_12L.map((ecg: any, idx: number) => (
                            <Card key={idx} className="mb-4 shadow-sm border-info">
                                <Card.Header className="bg-light fw-bold">
                                    ECG #{idx + 1} — <PersonalIdentifier identifier={ecg.result_time} />
                                </Card.Header>
                                <Card.Body>
                                    <ECG12LeadSection ecg={ecg} />
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                );

            case "Echo":
                return (
                    <div className="section-list">
                        {scanData.echo.map((echo: any, idx: number) => (
                            <Card key={idx} className="mb-4 shadow-sm border-primary">
                                <Card.Header className="bg-light fw-bold">
                                    Echo #{idx + 1} — <PersonalIdentifier identifier={echo.result_time} />
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-2">{echo.display_name}</div>
                                    <pre>{echo.narrative}</pre>
                                    <div><strong>LVEF:</strong>
                                        <ul>{Object.keys(echo.lvef).map(k => <li key={k}>{k}: {echo.lvef[k].value} {echo.lvef[k].unit}</li>)}</ul>
                                    </div>
                                    <div><strong>RVSP:</strong>
                                        <ul>{Object.keys(echo.rvsp).map(k => <li key={k}>{k}: {echo.rvsp[k].value} {echo.rvsp[k].unit}</li>)}</ul>
                                    </div>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                );

            case "Chest Radiology":
            case "Abdomen/Pelvis Radiology":
            case "Head Radiology":
                return (
                    <div className="section-list">
                        {scanData.imaging
                            .filter((study: any) => {
                                if (title === "Chest Radiology") return study.regions.includes("CHEST");
                                if (title === "Abdomen/Pelvis Radiology")
                                    return study.regions.includes("ABDOMEN") || study.regions.includes("PELVIS");
                                if (title === "Head Radiology") return study.regions.includes("HEAD");
                                return false;
                            })
                            .map((study: any, idx: number) => (
                                <Card key={idx} className="mb-4 shadow-sm border-secondary">
                                    <Card.Header className="bg-light fw-bold">
                                        {study.study} — <PersonalIdentifier identifier={getLocalDateTimeStr(study.result_time)} />
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-1 text-muted">
                                            ({getDaysBetweenDates(study.result_time, patientVisit.departure_time ?? new Date())} days ago)
                                        </div>
                                        <div><strong>Impression:</strong><pre>{study.impression}</pre></div>
                                    </Card.Body>
                                </Card>
                            ))}
                    </div>
                );

            case "Vital Signs":
                return (
                    <div className="section-list">
                        {Object.keys(scanData.vs_stats_by_csn).map((csn) => {
                            const vs = scanData.vs_stats_by_csn[csn];
                            if (!vs) return null;
                            return (
                                <Card key={csn} className="mb-4 shadow-sm border-success">
                                    <Card.Header className="bg-light fw-bold">
                                        Encounter (CSN {csn})
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-2">
                                            <small>
                                                ({getDaysBetweenDates(vs.start_time, patientVisit.departure_time ?? new Date())} days ago) •
                                                <PersonalIdentifier identifier={vs.start_time} /> to <PersonalIdentifier identifier={vs.end_time} /> •
                                                Dispo: {vs.dispo_class}
                                            </small>
                                        </div>
                                        <Row>
                                            <Col>{getVitalBox(vs.measures, "hr")}</Col>
                                            <Col>{getVitalBox(vs.measures, "nbps")}</Col>
                                            <Col>{getVitalBox(vs.measures, "nbpd")}</Col>
                                            <Col>{getVitalBox(vs.measures, "nbpm")}</Col>
                                        </Row>
                                        <Row>
                                            <Col>{getVitalBox(vs.measures, "rr")}</Col>
                                            <Col>{getVitalBox(vs.measures, "spo2")}</Col>
                                            <Col>{getVitalBox(vs.measures, "temp")}</Col>
                                            <Col>{getVitalBox(vs.measures, "perf")}</Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    </div>
                );


            default:
                return noDataIndicator;
        }
    };

    // ---------- Tiles ----------
    const sections = [
        { title: "Labs", icon: faVial },
        { title: "Echo", icon: faHeartbeat },
        { title: "ECG", icon: faWaveSquare },
        { title: "Chest Radiology", icon: faXRay },
        { title: "Abdomen/Pelvis Radiology", icon: faXRay },
        { title: "Head Radiology", icon: faXRay },
        { title: "Vital Signs", icon: faHeartbeat },
    ];

    return (
        <>
            <Container className="my-4 physiology-raw-data">
                <Row xs={1} sm={2} md={3} lg={3} className="g-3">
                    {sections.map(({ title, icon }) => (
                        <Col key={title}>
                            <Card
                                className="data-tile text-center shadow-sm hover-shadow"
                                onClick={() => setSelectedSection(title)}
                                style={{ cursor: "pointer", transition: "0.2s" }}
                            >
                                <Card.Body>
                                    <FontAwesomeIcon icon={icon} size="2x" className="mb-2 text-primary" />
                                    <p><strong>{title}</strong></p>
                                    <Badge bg="secondary">{getItemCount(title)}</Badge>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Modal for section details */}
            <Modal
                show={!!selectedSection}
                onHide={() => setSelectedSection(null)}
                size="xl"
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>{selectedSection}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSection && renderSection(selectedSection)}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedSection(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Labs Modal - using unified Labs.tsx component */}
            <LabsModal
                showModal={showLabsModal}
                setShowModal={setShowLabsModal}
                labs={transformedLabs}
                patientVisit={props.patientVisit}
                dataSource="snapshot"
            />
        </>
    );
};
